class CollectionFilters extends HTMLElement {
  constructor() {
    super();
    this.sectionId = null;
    this.sectionUrl = null;
    this.productGrid = null;
    this.filters = null;
    this.navButtons = null;
    this.filtersSlider = null;
    this.lastFilterItem = null;
    this.boundSection = null;
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.sectionUrl = this.dataset.sectionUrl;
    this.filtersSlider = this.querySelector('.tab-header');
    this.filters = this.querySelectorAll('a');
    this.productGrid = document.getElementById("product-grid");
    this.navButtons = this.querySelectorAll('.filters__nav-button');
    this.lastFilterItem = this.filters[this.filters.length - 1];
    this.boundSection = this.dataset.boundSectionClass.length > 0 ? document.querySelector(this.dataset.boundSectionClass) : false;

    this.setupEventListeners();

    const activeFilter = this.querySelector('a.active');
    if (activeFilter) this.scrollFilterIntoView(activeFilter, { instant: true });
  }

  setupEventListeners() {
    const desktop = window.matchMedia("(min-width: 990px)").matches;
    this.filters.forEach(filter => {
      const isCustomTag = filter.dataset.customTag === 'true';
      if (isCustomTag) {
        filter.addEventListener("click", (event) => this.handleCustomTagClick(event));
      } else {
        filter.addEventListener("click", (event) => this.handleFilterChange(event));
      }
    });

    this.navButtons.forEach(btn => {
      btn.addEventListener("click", (event) => this.handleNavClick(event));
    });

    if (desktop) {
      // Show nav buttons on desktop if last filter item is not fully visible
      const lastFilterItemRect = this.lastFilterItem.getBoundingClientRect();

      if (lastFilterItemRect.right + this.lastFilterItem.offsetWidth - 60 > window.innerWidth) this.navButtons.forEach(btn => btn.classList.remove('hidden'));
    }

  }

  handleNavClick(event) {
    const target = event.target.closest('.filters__nav-button');
    const type = target.dataset.direction;
    const scrollAmount = type == 'prev' ? -300 : 300;

    this.filtersSlider.scrollLeft += scrollAmount;

    this.toggleNavButtons();
  }

  toggleNavButtons() {
    const lastFilterItemRect = this.lastFilterItem.getBoundingClientRect();
    
    if (lastFilterItemRect.left > window.innerWidth || lastFilterItemRect.left < window.innerWidth)  this.navButtons.forEach(btn => btn.classList.toggle('active'));
  }

  updateActiveFilter(target){
    this.filters.forEach(el => el.classList.remove('active'));
    target.classList.add('active');
    this.scrollFilterIntoView(target);
  }

  scrollFilterIntoView(target, { instant = false } = {}) {
    if (!this.filtersSlider) return;

    const containerRect = this.filtersSlider.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const targetLeft = (targetRect.left - containerRect.left) + this.filtersSlider.scrollLeft;

    this.filtersSlider.scrollTo({
      left: targetLeft - containerRect.width / 2 + targetRect.width / 2,
      behavior: instant ? 'auto' : 'smooth'
    });
  }

  handleBoundSectionContentChange(params) {
    const basePath = this.sectionUrl || window.location.pathname;
    const url = `${basePath}?section_id=${this.boundSection.dataset.sectionId}&${params}`;

    fetch(url)
    .then(response => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.text();
    })
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newSectionContent = doc.getElementById(this.boundSection.id);

      if (newSectionContent) {
        this.boundSection.innerHTML = newSectionContent.innerHTML;
        this.notifyProductsUpdated();
      } else {
        console.error("New product grid content not found.");
      }
    })
    .catch(error => {
      console.error("Error fetching filtered products:", error);
    })
    .finally(() => {
      console.log("Fetch operation completed.");
    });
  }


  handleFilterChange(event) {
    event.preventDefault();
    let target = event.target.closest('a');
    const selectedFilter = target.dataset.params;

    this.updateActiveFilter(target);

    const basePath = this.sectionUrl || window.location.pathname;
    history.pushState(null, '', `${basePath}?${selectedFilter}`);

    // Fetch filtered products using Section Rendering API
    this.fetchFilteredProducts(selectedFilter);
    if (this.boundSection) this.handleBoundSectionContentChange(selectedFilter);
  }

  handleCustomTagClick(event){
    event.preventDefault();
    let target = event.target.closest('a');
    const tag = target.dataset.tag;
    const limit = parseInt(target.dataset.limit || '0', 10);

    this.updateActiveFilter(target);

    // Update URL without reloading (optional for UX consistency)
    history.pushState(null, '', `${this.sectionUrl}/${tag}`);

    const url = `${this.sectionUrl}/${tag}?section_id=${this.sectionId}`;

    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newProductGrid = doc.getElementById("product-grid");

        this.updateCollectionTitle(doc);

        if (newProductGrid) {
          if (limit > 0) {
            const items = Array.from(newProductGrid.querySelectorAll('.grid__item'));
            items.slice(limit).forEach(el => el.remove());
          }
          this.productGrid.innerHTML = newProductGrid.innerHTML;
          this.notifyProductsUpdated();
        } else {
          console.error("New product grid content not found.");
        }
      })
      .catch(error => {
        console.error("Error fetching tag filtered products:", error);
      })
      .finally(() => {
        console.log("Custom tag fetch completed.");
      });
  }

  fetchFilteredProducts(params) {
    const basePath = this.sectionUrl || window.location.pathname;
    const url = `${basePath}?section_id=${this.sectionId}&${params}`;
  
    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newProductGrid = doc.getElementById("product-grid");

        this.updateCollectionTitle(doc);
        this.updateFilterDescription(doc);

        if (newProductGrid) {
          this.productGrid.innerHTML = newProductGrid.innerHTML;
          this.notifyProductsUpdated();
        } else {
          console.error("New product grid content not found.");
        }
      })
      .catch(error => {
        console.error("Error fetching filtered products:", error);
      })
      .finally(() => {
        console.log("Fetch operation completed.");
      });
  }

  updateCollectionTitle (html){
    const source = html.querySelector('.collection-title');
    const destination = document.querySelector('.collection-title');

    if (destination && source) destination.innerHTML = source.innerHTML;
  }

  updateFilterDescription(html) {
    const source = html.querySelector('.collection-filter-description-wrapper');
    const destination = document.querySelector('.collection-filter-description-wrapper');

    if (destination && source) destination.innerHTML = source.innerHTML;
  }

  notifyProductsUpdated() {
    document.dispatchEvent(new CustomEvent('collection:products-updated'));
    if (
      window.CampaignSegmentation &&
      typeof window.CampaignSegmentation.reapplyProductSurfaces === 'function'
    ) {
      window.CampaignSegmentation.reapplyProductSurfaces();
    }
  }
  
}

document.addEventListener("DOMContentLoaded", () => {
  customElements.define("collection-filters", CollectionFilters);
});
